// eslint-disable-next-line no-shadow
export enum ErrorTypes {
  RejectPolicy = 'RejectPolicy',
  RejectData = 'RejectData',
  ValidationFailure = 'ValidationFailure',
  Refer = 'Refer',
  Warning = 'Warning',
}

export type ErrorsObject = {
  errorCode: string,
  errorType: keyof typeof ErrorTypes,
  errorMessage: string,
  linkedId?: string,
};

export type FlattenedErrorsObject = ErrorsObject & {
  id: string,
  propertyName: string,
};

export type ErrorsObjectArray = ErrorsObject[];
export type FlattenedErrorsObjectArray = FlattenedErrorsObject[];

export type ErrorType = {
  propertyName: string,
  errors: ErrorsObjectArray,
}

export type PropertyErrors = Record<string, string[]>;
export type ObjectErrors = string[];

export const defaultGuid = '00000000-0000-0000-0000-000000000000';

export type Errors = Record<string, ErrorType[]>;

export const errorsToPropertyErrors = (errors: ErrorType[]) => errors?.reduce((prev, current) => ({
  ...prev,
  [current.propertyName]: current.errors.map((e) => e.errorMessage),
}), {} as PropertyErrors);

export const errorsToObjectErrors = (errors: ErrorType[]) => errors?.filter((error) => (
  !error?.propertyName
))?.map((error) => error?.errors)
  ?.flat()
  ?.map((error) => error?.errorMessage);

export const flattenErrors = (errors: Errors): FlattenedErrorsObjectArray => Object.entries(errors ?? {})
  ?.map(([errorId, errorParent]) => errorParent?.map((e) => e?.errors?.map((error) => ({
    ...error,
    id: errorId,
    propertyName: e.propertyName,
  }))))
  ?.flat()
  ?.flat();

export const flattenErrorTypeArray = (errors: ErrorType[]): FlattenedErrorsObjectArray => Object.entries(errors ?? {})
  ?.map(([errorId, errorParent]) => errorParent?.errors?.map((error) => ({
    ...error,
    id: errorId,
    propertyName: errorParent.propertyName,
  })))
  ?.flat();

function GroupErrorsByType(errors: FlattenedErrorsObject[], filterFn: (current: FlattenedErrorsObject) => boolean) {
  return errors?.reduce((prev, current) => ({
    ...prev,
    [current.errorType]: [
      ...(prev?.[current?.errorType] ? prev[current?.errorType] : []),
      ...(filterFn(current) ? [current] : []),
    ],
  }), {} as Record<keyof typeof ErrorTypes, FlattenedErrorsObject[]>);
}

export function groupErrorsByNonProperty(errors: FlattenedErrorsObject[]) {
  return GroupErrorsByType(errors, (current) => !current?.propertyName);
}

export function groupErrorsByProperty(errors: FlattenedErrorsObject[]) {
  return GroupErrorsByType(errors, (current) => !!current?.propertyName);
}
